import React, { useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "../ui/dialog";
import ImageUpload from "./ImageUpload";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../ui/alert-dialog";

// Generic CRUD manager for a content collection.
export default function EntityManager({ type, title, items, fields, onChanged, renderCell }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const startAdd = () => {
    const init = {};
    fields.forEach((f) => (init[f.key] = f.type === "number" ? 0 : ""));
    setForm(init);
    setEditing(null);
    setOpen(true);
  };
  const startEdit = (item) => {
    setForm({ ...item });
    setEditing(item.id);
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      fields.forEach((f) => {
        if (f.type === "number") payload[f.key] = parseInt(payload[f.key], 10) || 0;
      });
      if (editing) await api.update(type, editing, payload);
      else await api.create(type, payload);
      toast.success(editing ? "Updated successfully" : "Created successfully");
      setOpen(false);
      await onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    try {
      await api.remove(type, id);
      toast.success("Deleted");
      await onChanged();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div data-testid={`manager-${type}`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-3xl text-foreground">{title}</h2>
          <p className="text-sm text-muted-foreground mt-1">{items.length} item{items.length !== 1 ? "s" : ""}</p>
        </div>
        <Button onClick={startAdd} data-testid={`add-${type}-btn`} className="bg-primary text-primary-foreground gap-2">
          <Plus className="h-4 w-4" /> Add New
        </Button>
      </div>

      <div className="grid gap-3">
        {items.map((item) => (
          <div key={item.id} className="flex items-center gap-4 bg-card border border-border p-4 rounded-sm" data-testid={`row-${type}-${item.id}`}>
            {item.image && <img src={item.image} alt="" className="h-14 w-14 object-cover rounded-sm shrink-0" />}
            <div className="min-w-0 flex-1">
              {renderCell ? renderCell(item) : (
                <>
                  <p className="font-medium text-foreground truncate">{item.name || item.title}</p>
                  <p className="text-sm text-muted-foreground truncate">{item.category || item.role || item.date || item.quote || item.description}</p>
                </>
              )}
            </div>
            <Button variant="outline" size="icon" onClick={() => startEdit(item)} data-testid={`edit-${type}-btn`}>
              <Pencil className="h-4 w-4" />
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="icon" className="text-destructive" data-testid={`delete-${type}-btn`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this item?</AlertDialogTitle>
                  <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => del(item.id)} data-testid={`confirm-delete-${type}`} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ))}
        {items.length === 0 && <p className="text-muted-foreground py-8 text-center">No items yet. Click "Add New".</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit" : "Add"} {title}</DialogTitle>
            <DialogDescription>Fill in the details below and click Save.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {fields.map((f) => (
              <div key={f.key}>
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">{f.label}</Label>
                {f.type === "image" ? (
                  <div className="mt-1.5">
                    <ImageUpload value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} testid={f.key} />
                  </div>
                ) : f.type === "textarea" ? (
                  <Textarea className="mt-1.5" value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`field-${f.key}`} />
                ) : (
                  <Input type={f.type === "number" ? "number" : "text"} className="mt-1.5" value={form[f.key] ?? ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} data-testid={`field-${f.key}`} />
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid={`save-${type}-btn`} className="bg-primary text-primary-foreground">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
